/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { isHttpsUrl } from "@cosmos/common-utils";

/**
 * Embed variant types for different video platforms
 */
export enum EmbedVariantEnum {
	Miscellaneous = "miscellaneous",
	Prohibited = "prohibited",
	Vimeo = "vimeo",
	YouTube = "youtube",
}

/**
 * Converts any YouTube URL format to the standardized embed format with youtube-nocookie.com domain.
 * Preserves all query parameters from the original URL.
 *
 * Supported formats:
 * - https://www.youtube.com/embed/VIDEO_ID
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://m.youtube.com/watch?v=VIDEO_ID
 *
 * @param url - The YouTube URL to convert
 * @returns The converted embed URL with youtube-nocookie.com domain, or empty string if invalid
 */
export const convertYouTubeToEmbed = (url: string): string => {
	if (!url || typeof url !== "string") {
		return "";
	}

	try {
		const urlObj = new URL(url);
		let videoId: string | null = null;

		// Handle youtube.com/watch?v=VIDEO_ID format
		if (urlObj.searchParams.get("v") && urlObj.pathname === "/watch") {
			videoId = urlObj.searchParams.get("v");
			urlObj.searchParams.delete("v");
		}
		// Handle youtu.be/VIDEO_ID and youtube.com/embed/VIDEO_ID formats
		// In both cases, the video ID is the last non-empty path segment
		else if (
			urlObj.hostname === "youtu.be" ||
			urlObj.hostname === "www.youtu.be" ||
			urlObj.pathname.startsWith("/embed/")
		) {
			const pathSegments = urlObj.pathname.split("/").filter(Boolean);
			const lastSegment = pathSegments[pathSegments.length - 1];
			// The last segment should exist and should not be "embed" itself
			videoId = lastSegment && lastSegment !== "embed" ? lastSegment : null;
		}

		if (!videoId) return "";

		urlObj.pathname = `/embed/${videoId}`;
		urlObj.hostname = "www.youtube-nocookie.com";

		return urlObj.toString();
	} catch {
		return "";
	}
};

/**
 * Extracts the video ID from a YouTube embed URL.
 *
 * @param embedUrl - The YouTube embed URL (e.g., "https://www.youtube-nocookie.com/embed/VIDEO_ID?params")
 * @returns The extracted video ID, or null if invalid
 */
export const extractYouTubeVideoId = (embedUrl: string): string | null => {
	if (!embedUrl || typeof embedUrl !== "string") {
		return null;
	}

	// Use regex to extract video ID from /embed/VIDEO_ID format
	const match = embedUrl.match(/\/embed\/([^?]+)/);
	return match ? match[1] : null;
};

/**
 * Asynchronously determines the best available YouTube thumbnail for a video.
 * Attempts to load the maxresdefault (1280x720) version first, falling back to hqdefault (480x360).
 *
 * YouTube returns a small 120x90px placeholder image when maxresdefault doesn't exist,
 * so we check the loaded image width to determine if it's a real thumbnail.
 *
 * @param videoId - The YouTube video ID
 * @returns Promise that resolves to the best available thumbnail URL
 */
export const getYouTubeThumbnailUrl = async (videoId: string): Promise<string> => {
	const maxResUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
	const fallbackUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

	return new Promise((resolve) => {
		const img = new Image();
		img.src = maxResUrl;

		img.onload = () => {
			// maxresdefault returns a tiny 120x90 px image on 404
			// if the width is greater than 120, it's a real thumbnail
			if (img.width > 120) {
				resolve(maxResUrl);
			} else {
				resolve(fallbackUrl);
			}
		};

		img.onerror = () => {
			resolve(fallbackUrl);
		};
	});
};

/**
 * Builds the final iframe source URL with platform-specific configurations.
 * Adds autoplay, privacy, and mute parameters based on the embed variant.
 *
 * @param embedVariant - The type of embed (YouTube, Vimeo, etc.)
 * @param iframeSrc - The original iframe source URL
 * @param mute - Whether to mute the video on autoplay
 * @returns The configured iframe source URL, or empty string if invalid
 */
export const getFinalIframeSrc = (
	embedVariant: EmbedVariantEnum,
	iframeSrc?: string | null,
	mute?: boolean,
): string => {
	if (!iframeSrc || !isHttpsUrl(iframeSrc)) return "";
	const iframeUrl = new URL(iframeSrc);

	switch (embedVariant) {
		case EmbedVariantEnum.Vimeo: {
			iframeUrl.searchParams.set("dnt", "true");
			iframeUrl.searchParams.set("autoplay", "1");
			if (mute) {
				iframeUrl.searchParams.set("muted", "1");
			}

			return iframeUrl.toString();
		}
		case EmbedVariantEnum.YouTube: {
			const embedUrl = convertYouTubeToEmbed(iframeSrc);
			if (!embedUrl) return "";

			const embedUrlObj = new URL(embedUrl);
			embedUrlObj.searchParams.set("rel", "0");
			embedUrlObj.searchParams.set("autoplay", "1");
			if (mute) {
				embedUrlObj.searchParams.set("mute", "1");
			}

			return embedUrlObj.toString();
		}
		default:
			return iframeSrc;
	}
};
